
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Session } from '@supabase/supabase-js';
import { logSecurityEvent } from '@/utils/securityAudit';

interface AuthContextType {
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  authError: Error | null;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [sessionCheckInterval, setSessionCheckInterval] = useState<NodeJS.Timeout | null>(null);

  const checkAdminStatus = async (currentSession?: Session | null) => {
    try {
      const activeSession = currentSession || session;
      if (!activeSession?.user) {
        setIsAdmin(false);
        return;
      }

      const { data, error } = await supabase.rpc('is_admin');
      if (error) {
        await logSecurityEvent({
          event_type: 'ADMIN_CHECK_FAILED',
          details: { error: error.message, user_id: activeSession.user.id }
        });
        throw error;
      }

      const adminStatus = !!data;
      setIsAdmin(adminStatus);
      setAuthError(null);

      // Log admin status changes
      if (adminStatus) {
        await logSecurityEvent({
          event_type: 'ADMIN_ACCESS_GRANTED',
          details: { user_id: activeSession.user.id }
        });
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
      setAuthError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSession = async () => {
    try {
      const { data: { session: newSession }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        await logSecurityEvent({
          event_type: 'SESSION_REFRESH_FAILED',
          details: { error: error.message }
        });
        throw error;
      }

      setSession(newSession);
      if (newSession?.user) {
        await checkAdminStatus(newSession);
      }
    } catch (error) {
      console.error('Session refresh failed:', error);
      setAuthError(error as Error);
    }
  };

  const validateSession = async (currentSession: Session | null) => {
    if (!currentSession) return;

    try {
      // Check if session is still valid by making a simple authenticated request
      const { error } = await supabase.from('profiles').select('id').limit(1);
      
      if (error && error.message.includes('JWT')) {
        await logSecurityEvent({
          event_type: 'SESSION_INVALID',
          details: { user_id: currentSession.user.id }
        });
        
        // Session is invalid, try to refresh
        await refreshSession();
      }
    } catch (error) {
      console.error('Session validation failed:', error);
    }
  };

  useEffect(() => {
    let mounted = true;
    
    const setupAuth = async () => {
      setIsLoading(true);
      
      // Set up auth state listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, newSession) => {
          if (!mounted) return;
          
          console.log('Auth event:', event, newSession?.user?.id);
          
          setSession(newSession);
          
          if (event === 'SIGNED_IN' && newSession) {
            await logSecurityEvent({
              event_type: 'USER_SIGNED_IN',
              details: { user_id: newSession.user.id }
            });
            await checkAdminStatus(newSession);
          } else if (event === 'SIGNED_OUT') {
            await logSecurityEvent({
              event_type: 'USER_SIGNED_OUT',
              details: {}
            });
            setIsAdmin(false);
            setAuthError(null);
          }
          
          setIsLoading(false);
        }
      );
      
      // Check for existing session
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      
      if (mounted) {
        setSession(existingSession);
        
        if (existingSession?.user) {
          await checkAdminStatus(existingSession);
          
          // Set up periodic session validation (every 5 minutes)
          const interval = setInterval(() => {
            validateSession(existingSession);
          }, 5 * 60 * 1000);
          
          setSessionCheckInterval(interval);
        } else {
          setIsLoading(false);
        }
      }
      
      return () => {
        subscription.unsubscribe();
      };
    };
    
    setupAuth();
    
    return () => {
      mounted = false;
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval);
      }
    };
  }, []);

  const signOut = async () => {
    try {
      setIsLoading(true);
      
      if (session?.user) {
        await logSecurityEvent({
          event_type: 'USER_SIGNOUT_INITIATED',
          details: { user_id: session.user.id }
        });
      }

      // Clear session check interval
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval);
        setSessionCheckInterval(null);
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        await logSecurityEvent({
          event_type: 'SIGNOUT_FAILED',
          details: { error: error.message }
        });
        throw error;
      }

      // Clear local state
      setSession(null);
      setIsAdmin(false);
      setAuthError(null);
    } catch (error) {
      console.error("Error signing out:", error);
      setAuthError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    session,
    isAdmin,
    isLoading,
    authError,
    signOut,
    refreshSession
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
